import { Children, isValidElement, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

// WebView2 composition does not reliably render the native HTML select popup.
// Hosted account controls keep their menu in the rendered page instead.
export function AccountSelect({ hosted = false, children, value, onValueChange, id, disabled, ...aria }: {
  hosted?: boolean; children: ReactNode; value?: string; onValueChange: (value: string) => void;
  id: string; disabled?: boolean; 'aria-label'?: string; 'aria-describedby'?: string;
}) {
  const options = Children.toArray(children).flatMap(child => {
    if (!isValidElement<{ value: string; children: ReactNode; disabled?: boolean }>(child)) return [];
    return [{ value: child.props.value, label: String(child.props.children), disabled: child.props.disabled === true }];
  });
  const [open, setOpen] = useState(false), [active, setActive] = useState(0);
  const [position, setPosition] = useState({ left: 0, top: 0, width: 0, maxHeight: 288 });
  const button = useRef<HTMLButtonElement>(null), menu = useRef<HTMLDivElement>(null);
  const anchor = useRef({left: 0, top: 0});
  const latest = useRef(options); latest.current = options;
  const typeahead = useRef({ text: '', at: 0 });
  const signature = JSON.stringify(options);
  useEffect(() => setOpen(false), [disabled, signature]);
  useLayoutEffect(() => {
    if (!open || !button.current) return;
    const box = button.current.getBoundingClientRect();
    anchor.current = {left: box.left, top: box.top};
    const below = window.innerHeight - box.bottom - 8, above = box.top - 8;
    const height = Math.max(40, Math.min(288, below >= 160 || below >= above ? below : above));
    const width = Math.min(360, box.width, window.innerWidth - 16);
    setPosition({ left: Math.min(box.left, window.innerWidth - width - 8), top: below >= 160 || below >= above ? box.bottom + 4 : box.top - height - 4, width, maxHeight: height });
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const outside = (event: Event) => {
      if (!button.current?.contains(event.target as Node) && !menu.current?.contains(event.target as Node)) setOpen(false);
    };
    const resized = () => setOpen(false);
    const scrolled = (event: Event) => {
      if (menu.current?.contains(event.target as Node)) return;
      const box = button.current?.getBoundingClientRect();
      // A focus/scroll-into-view event can arrive after opening. Only dismiss
      // when the anchor actually moved, not for that already-applied scroll.
      if (!box || Math.abs(box.top - anchor.current.top) > 1 || Math.abs(box.left - anchor.current.left) > 1) setOpen(false);
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('focusin', outside);
    document.addEventListener('scroll', scrolled, true);
    window.addEventListener('resize', resized);
    return () => {
      document.removeEventListener('pointerdown', outside); document.removeEventListener('focusin', outside);
      document.removeEventListener('scroll', scrolled, true); window.removeEventListener('resize', resized);
    };
  }, [open]);
  useEffect(() => { if (open) menu.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' }); }, [active, open]);
  function expand() {
    if (disabled || button.current?.matches(':disabled')) return;
    setActive(Math.max(0, options.findIndex(option => option.value === value))); setOpen(true);
  }
  function choose(index: number) {
    const option = latest.current[index];
    if (disabled || button.current?.matches(':disabled') || !option || option.disabled) return;
    onValueChange(option.value); setOpen(false); button.current?.focus();
  }
  if (!hosted) return <select id={id} value={value} disabled={disabled} {...aria} onChange={event => onValueChange(event.target.value)}>{children}</select>;
  return <>
    <button ref={button} id={id} type="button" role="combobox" className="account-select" disabled={disabled}
      {...aria} aria-expanded={open} aria-controls={open ? `${id}-options` : undefined} aria-haspopup="listbox"
      aria-activedescendant={open ? `${id}-option-${active}` : undefined}
      onClick={() => open ? setOpen(false) : expand()}
      onKeyDown={event => {
        if (event.key === 'Escape' && open) { event.preventDefault(); event.stopPropagation(); setOpen(false); return; }
        if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          if (!open) expand();
          else setActive(current => event.key === 'Home' ? 0 : event.key === 'End' ? options.length - 1 : Math.max(0, Math.min(options.length - 1, current + (event.key === 'ArrowDown' ? 1 : -1))));
        } else if ((event.key === 'Enter' || event.key === ' ') && open) { event.preventDefault(); choose(active); }
        else if (event.key === 'Tab') setOpen(false);
        else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && event.key !== ' ') {
          event.preventDefault(); const now = Date.now();
          typeahead.current = { text: (now - typeahead.current.at < 700 ? typeahead.current.text : '') + event.key.toLowerCase(), at: now };
          const match = options.findIndex(option => option.label.toLowerCase().startsWith(typeahead.current.text));
          if (!open) expand(); if (match >= 0) setActive(match);
        }
      }}><span>{options.find(option => option.value === value)?.label ?? 'Choose an option'}</span><span aria-hidden="true">⌄</span></button>
    {open && createPortal(<div ref={menu} id={`${id}-options`} role="listbox" aria-labelledby={id} className="account-select-menu" style={position}>
      {options.map((option, index) => <div key={option.value} id={`${id}-option-${index}`} role="option" aria-selected={option.value === value} aria-disabled={option.disabled || undefined}
        data-index={index} data-active={active === index} onMouseDown={event => event.preventDefault()}
        onPointerMove={() => setActive(index)} onClick={() => choose(index)}>
        <span>{option.label}</span><span aria-hidden="true">{option.value === value ? '✓' : ''}</span>
      </div>)}
    </div>, document.body)}
  </>;
}
